/**
 * Citations Dropdown Component
 * Displays citations from search results and knowledge graph in a collapsible dropdown
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Collapse,
  IconButton,
  Typography,
  Chip,
  Divider,
  Paper,
  Stack,
  Tooltip,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Verified as VerifiedIcon,
  AutoStories as KnowledgeIcon,
  Language as WebIcon,
  ContentCopy as CopyIcon,
  FormatQuote as QuoteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Types for citations
export interface Citation {
  id: string;
  url?: string;
  title: string;
  domain?: string;
  snippet: string;
  publishedDate?: string;
  author?: string;
  favicon?: string;
  retrievedAt?: string;
  relevanceScore?: number;
  safetyScore?: number;
  isVerified?: boolean;
  engine?: string;
  source?: 'web' | 'knowledge' | 'knowledge_graph' | 'document';
  nodeId?: string; // For knowledge graph citations
  nodeType?: string; // For knowledge graph citations
}

interface CitationsDropdownProps {
  citations: Citation[];
  defaultExpanded?: boolean;
  maxHeight?: number;
}

// Styled components
const CitationContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    transform: 'translateY(-1px)'
  }
}));

const CitationHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12)
  }
}));

const CitationNumber = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  minWidth: 32,
  height: 24,
  '& .MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8
  }
}));

const CitationsDropdown: React.FC<CitationsDropdownProps> = ({
  citations,
  defaultExpanded = false,
  maxHeight = 400
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  if (!citations || citations.length === 0) {
    return null;
  }

  // Group citations by source
  const webCitations = citations.filter(c => c.source === 'web' || c.url?.startsWith('http'));
  const knowledgeCitations = citations.filter(c => c.source === 'knowledge' || c.source === 'knowledge_graph' || c.nodeId);
  const documentCitations = citations.filter(c => c.source === 'document');

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleCopyCitation = (citation: Citation) => {
    const citationText = `${citation.title}. ${citation.domain || 'Source'}. ${citation.url || 'No URL'}`;
    navigator.clipboard.writeText(citationText);
    setCopiedId(citation.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleOpenUrl = (url?: string) => {
    if (url) {
      // Navigate to knowledge graph viewer for knowledge:// URLs
      if (url.startsWith('knowledge://')) {
        // Extract node ID from URL format: knowledge://node/[nodeId]
        const nodeId = url.replace('knowledge://node/', '');
        // Navigate to knowledge graph page with node ID as query parameter
        navigate(`/knowledge-graph?nodeId=${encodeURIComponent(nodeId)}`);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getSourceIcon = (source?: string) => {
    switch (source) {
      case 'knowledge':
      case 'knowledge_graph':
        return <KnowledgeIcon fontSize="small" color="primary" />;
      case 'web':
        return <WebIcon fontSize="small" color="action" />;
      default:
        return <QuoteIcon fontSize="small" />;
    }
  };

  const renderCitation = (citation: Citation, index: number) => (
    <CitationContainer
      key={citation.id}
      elevation={0}
      onClick={() => handleOpenUrl(citation.url)}
    >
      <Stack spacing={1}>
        {/* Citation Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
            <CitationNumber
              label={citation.id}
              size="small"
              color="primary"
              variant="outlined"
            />

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {getSourceIcon(citation.source)}

                <Typography variant="subtitle2" component="span" fontWeight={600}>
                  {citation.title}
                </Typography>

                {citation.isVerified && (
                  <Tooltip title="Verified Source">
                    <VerifiedIcon fontSize="small" color="success" />
                  </Tooltip>
                )}
              </div>

              {/* Domain/Source Info */}
              <Typography variant="caption" color="text.secondary" component="div">
                {citation.domain || citation.nodeType || 'Unknown Source'}
                {citation.publishedDate && ` • ${new Date(citation.publishedDate).toLocaleDateString()}`}
                {citation.author && ` • ${citation.author}`}
              </Typography>
            </div>
          </div>

          {/* Action Buttons */}
          <Stack direction="row" spacing={0.5}>
            {citation.url && (
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenUrl(citation.url);
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={copiedId === citation.id ? "Copied!" : "Copy citation"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyCitation(citation);
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </div>

        {/* Snippet */}
        <Typography variant="body2" color="text.secondary">
          {citation.snippet}
        </Typography>

        {/* Metadata */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {citation.relevanceScore !== undefined && (
            <Chip
              label={`Relevance: ${Math.round(citation.relevanceScore * 100)}%`}
              size="small"
              variant="outlined"
              color={citation.relevanceScore > 0.7 ? 'success' : 'default'}
            />
          )}

          {citation.safetyScore !== undefined && (
            <Chip
              label={`Safety: ${Math.round(citation.safetyScore * 100)}%`}
              size="small"
              variant="outlined"
              color={citation.safetyScore > 0.8 ? 'success' : 'warning'}
            />
          )}

          {citation.engine && (
            <Chip
              label={citation.engine}
              size="small"
              variant="outlined"
            />
          )}

          {citation.nodeId && (
            <Chip
              label="Knowledge Graph"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </div>
      </Stack>
    </CitationContainer>
  );

  return (
    <div style={{ marginTop: 16 }}>
      {/* Header - Always Visible */}
      <CitationHeader onClick={handleToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Citations
          </Typography>
          <Chip
            label={citations.length}
            size="small"
            color="primary"
            style={{ height: 20 }}
          />

          {/* Source breakdown */}
          {webCitations.length > 0 && (
            <Chip
              icon={<WebIcon />}
              label={webCitations.length}
              size="small"
              variant="outlined"
              style={{ height: 20 }}
            />
          )}
          {knowledgeCitations.length > 0 && (
            <Chip
              icon={<KnowledgeIcon />}
              label={knowledgeCitations.length}
              size="small"
              variant="outlined"
              color="primary"
              style={{ height: 20 }}
            />
          )}
        </div>

        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </CitationHeader>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        <div
          style={{
            marginTop: 8,
            maxHeight: maxHeight,
            overflowY: 'auto',
            paddingLeft: 8,
            paddingRight: 8,
          }}
        >
          {/* Knowledge Graph Citations */}
          {knowledgeCitations.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="primary"
                fontWeight={600}
                style={{ display: 'block', marginBottom: 8, marginTop: 8 }}
              >
                KNOWLEDGE GRAPH SOURCES
              </Typography>
              {knowledgeCitations.map((citation, idx) =>
                renderCitation(citation, idx)
              )}
            </>
          )}

          {/* Web Citations */}
          {webCitations.length > 0 && (
            <>
              {knowledgeCitations.length > 0 && <Divider style={{ marginTop: 16, marginBottom: 16 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                style={{ display: 'block', marginBottom: 8 }}
              >
                WEB SOURCES
              </Typography>
              {webCitations.map((citation, idx) =>
                renderCitation(citation, knowledgeCitations.length + idx)
              )}
            </>
          )}

          {/* Document Citations */}
          {documentCitations.length > 0 && (
            <>
              {(knowledgeCitations.length > 0 || webCitations.length > 0) && <Divider style={{ marginTop: 16, marginBottom: 16 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                style={{ display: 'block', marginBottom: 8 }}
              >
                DOCUMENT SOURCES
              </Typography>
              {documentCitations.map((citation, idx) =>
                renderCitation(citation, knowledgeCitations.length + webCitations.length + idx)
              )}
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default CitationsDropdown;
